"use client";

import { c as _c } from "react/compiler-runtime";
import { IconMessageCircleFilled } from "@tabler/icons-react";
import { formatForDisplay } from "@tanstack/react-hotkeys";
import { useAnimationFrame, useMotionValue, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import { useState } from "react";
import { Button } from "@domainstack/ui/button";
import { Separator } from "@domainstack/ui/separator";
import { Spinner } from "@domainstack/ui/spinner";
const MotionButton = m.create(Button);
export const CHAT_HOTKEY = "Mod+I";
interface ChatFabProps {
  loading?: boolean;
  onClick: () => void;
}
interface ChatFabButtonProps extends Required<ChatFabProps> {
  children: React.ReactNode;
  className: string;
  onHoverEnd?: () => void;
  onHoverStart?: () => void;
  size: "default" | "icon-lg";
}
function ChatFabButton(t0) {
  const $ = _c(15);
  const {
    children,
    className,
    loading,
    onClick,
    onHoverEnd,
    onHoverStart,
    size
  } = t0;
  const prefersReducedMotion = useReducedMotion();
  let t1;
  if ($[0] !== prefersReducedMotion) {
    t1 = prefersReducedMotion ? false : {
      opacity: 0,
      y: 16
    };
    $[0] = prefersReducedMotion;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 1,
      y: 0
    };
    t3 = {
      opacity: 0,
      y: 16
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  const t4 = prefersReducedMotion ? undefined : "hover";
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      duration: 0.25,
      ease: [0.22, 1, 0.36, 1]
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== children || $[6] !== className || $[7] !== loading || $[8] !== onClick || $[9] !== onHoverEnd || $[10] !== onHoverStart || $[11] !== size || $[12] !== t1 || $[13] !== t4) {
    t6 = <MotionButton variant="outline" size={size} aria-label="Ask AI" aria-busy={loading} disabled={loading} className={className} onClick={onClick} onHoverEnd={onHoverEnd} onHoverStart={onHoverStart} initial={t1} animate={t2} exit={t3} whileHover={t4} transition={t5}>{children}</MotionButton>;
    $[5] = children;
    $[6] = className;
    $[7] = loading;
    $[8] = onClick;
    $[9] = onHoverEnd;
    $[10] = onHoverStart;
    $[11] = size;
    $[12] = t1;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

// Static: the gradient itself never changes, so it never needs repainting.
// Rendered oversized and moved with transforms (compositor-only) instead of
// animating the `background` positions directly (paint-only, much costlier).
const MESH_GRADIENT_BACKGROUND = `
  radial-gradient(ellipse at 25% 30%, rgba(56, 189, 248, 0.8) 0%, transparent 50%),
  radial-gradient(ellipse at 75% 65%, rgba(139, 92, 246, 0.7) 0%, transparent 55%),
  radial-gradient(ellipse at 55% 20%, rgba(236, 72, 153, 0.6) 0%, transparent 45%),
  radial-gradient(ellipse at 40% 80%, rgba(34, 197, 94, 0.5) 0%, transparent 50%)
`;
function useMeshGradientMotion(active) {
  const $ = _c(11);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useMotionValue(0);
  const scale = useMotionValue(1);
  let t0;
  if ($[0] !== active || $[1] !== rotate || $[2] !== scale || $[3] !== x || $[4] !== y) {
    t0 = time => {
      if (!active) {
        return;
      }
      const t = time / 1000;
      x.set(Math.sin(t * 0.3) * 12 + Math.sin(t * 0.7 + 2) * 6);
      y.set(Math.cos(t * 0.4 + 1) * 12 + Math.cos(t * 0.9 + 3) * 6);
      rotate.set(Math.sin(t * 0.2) * 15);
      scale.set(1 + Math.sin(t * 0.5 + 4) * 0.08);
    };
    $[0] = active;
    $[1] = rotate;
    $[2] = scale;
    $[3] = x;
    $[4] = y;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  useAnimationFrame(t0);
  let t1;
  if ($[6] !== rotate || $[7] !== scale || $[8] !== x || $[9] !== y) {
    t1 = {
      x,
      y,
      rotate,
      scale
    };
    $[6] = rotate;
    $[7] = scale;
    $[8] = x;
    $[9] = y;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
function MobileChatFab(t0) {
  const $ = _c(6);
  const {
    loading,
    onClick
  } = t0;
  let t1;
  if ($[0] !== loading) {
    t1 = <span className="relative z-10 flex items-center justify-center">{loading ? <Spinner className="size-4 text-foreground/90" /> : <IconMessageCircleFilled className="size-4 text-foreground/90" aria-hidden="true" />}</span>;
    $[0] = loading;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== loading || $[3] !== onClick || $[4] !== t1) {
    t2 = <ChatFabButton loading={loading} onClick={onClick} size="icon-lg" className="group fixed right-6 bottom-6 z-40 overflow-hidden rounded-full shadow-lg backdrop-blur-md transition-none md:hidden">{t1}</ChatFabButton>;
    $[2] = loading;
    $[3] = onClick;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function DesktopChatFab(t0) {
  const $ = _c(17);
  const {
    loading,
    onClick
  } = t0;
  const prefersReducedMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const meshGradient = useMeshGradientMotion(hovered && !prefersReducedMotion);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setHovered(true);
    t2 = () => setHovered(false);
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0
    };
    t4 = {
      hover: {
        opacity: 1
      }
    };
    t5 = {
      duration: 0.2
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== meshGradient.rotate || $[6] !== meshGradient.scale || $[7] !== meshGradient.x || $[8] !== meshGradient.y) {
    t6 = <m.span className="pointer-events-none absolute inset-[-50%] rounded-[inherit]" initial={t3} variants={t4} transition={t5} style={{
      background: MESH_GRADIENT_BACKGROUND,
      filter: "blur(4px)",
      x: meshGradient.x,
      y: meshGradient.y,
      rotate: meshGradient.rotate,
      scale: meshGradient.scale
    }} />;
    $[5] = meshGradient.rotate;
    $[6] = meshGradient.scale;
    $[7] = meshGradient.x;
    $[8] = meshGradient.y;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-[13px] leading-none font-semibold tracking-tight">Ask AI</span>;
    t8 = <Separator orientation="vertical" className="bg-border/80 group-hover:bg-foreground/40 data-[orientation=vertical]:h-4" />;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="relative z-10 flex items-center gap-2">{t7}{t8}<kbd className="pointer-events-none font-sans text-xs leading-none font-medium text-foreground/70 select-none">{formatForDisplay(CHAT_HOTKEY, {
          separatorToken: "\xA0"
        })}</kbd></span>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== loading || $[14] !== onClick || $[15] !== t6) {
    t10 = <ChatFabButton loading={loading} onClick={onClick} onHoverStart={t1} onHoverEnd={t2} size="default" className="group fixed right-6 bottom-6 z-40 hidden h-8 overflow-hidden px-2.5 shadow-lg backdrop-blur-md transition-none md:flex">{t6}{t9}</ChatFabButton>;
    $[13] = loading;
    $[14] = onClick;
    $[15] = t6;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
}
export function ChatFab(t0) {
  const $ = _c(3);
  const {
    loading: t1,
    onClick
  } = t0;
  const loading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== loading || $[1] !== onClick) {
    t2 = <><MobileChatFab loading={loading} onClick={onClick} /><DesktopChatFab loading={loading} onClick={onClick} /></>;
    $[0] = loading;
    $[1] = onClick;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}